if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="id"]');
      this.submitButton = this.querySelector('button[type="submit"][name="add"]');
      this.errorMessages = this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.querySelector('.product-form__error-message');

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      // Handle secondary button (Buy It Now) clicks
      this.querySelectorAll('[data-skip-cart-button]').forEach(button => {
        button.addEventListener('click', this.onSkipCartClick.bind(this));
      });
    }

    async onSubmit(event) {
      event.preventDefault();
      event.stopPropagation();

      // required custom fields (chug-components.js custom-product-field)
      const scope0 = this.closest('.shopify-section') || document;
      const invalidField = [...scope0.querySelectorAll('custom-product-field')]
        .find(f => typeof f.validate === 'function' && !f.validate());
      if (invalidField) {
        this.handleErrorMessage(invalidField.dataset.atcErrorMsg || 'Please fill the required field.');
        return;
      }

      // A2 (audit 2026-10-08): in-flight guard. The sticky bar's ATC button is bound to this
      // form via form="…" but lives OUTSIDE <product-form>, so it was never disabled and a
      // double tap sent two add requests (qty 2 charged). One submit at a time, and every
      // button that submits this form — inside or outside — is disabled while it runs.
      if (this._submitting) return;
      this._submitting = true;
      const buttons = this.getSubmitButtons();
      buttons.forEach(btn => {
        btn.disabled = true;
        btn.classList.add('loading');
      });

      // Ensure we have the variant ID
      let variantInput = this.form.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) {
        const variantsDiv = this.form.querySelector('.product-form__variants');
        if (variantsDiv && variantsDiv.dataset.selectedVariant) {
          variantInput = document.createElement('input');
          variantInput.type = 'hidden';
          variantInput.name = 'id';
          variantInput.value = variantsDiv.dataset.selectedVariant;
          this.form.appendChild(variantInput);
        }
      }

      // Build form data manually to ensure all fields are captured
      const formData = new FormData();

      const variantId = variantInput ? variantInput.value : null;
      if (!variantId) {
        console.error('No variant ID found');
        this.handleErrorMessage('Please select a product variant first.');
        buttons.forEach(btn => {
          btn.disabled = false;
          btn.classList.remove('loading');
        });
        this._submitting = false;
        return;
      }
      formData.append('id', variantId);

      // Add quantity — the bundle / quantity-breaks picker (<quantity-breaks>) is a sibling
      // of this form, so read the *checked* quantity radio in the product section first,
      // then fall back to a standard quantity input/stepper.
      const quantity = this.getSelectedQuantity();
      formData.append('quantity', quantity);

      // Add properties
      this.form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]').forEach(propInput => {
        if (propInput.value) {
          formData.append(propInput.name, propInput.value);
        }
      });

      // Add selling plan if present
      const sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
      if (sellingPlanInput && sellingPlanInput.value) {
        formData.append('selling_plan', sellingPlanInput.value);
      }

      console.log('Form data - variant ID:', variantId, 'quantity:', quantity);

      // Toggled-on upsells (upsell-system.js) and unlocked quantity-gifts
      // (chug-components.js) get added with the main product in one items call.
      let upsellIds = this.getSelectedUpsellIds();
      const giftIds = await this.getUnlockedGiftIds();
      upsellIds = upsellIds.concat(giftIds, this.getTierUpsellIds());

      try {
        // Use /cart/add.js endpoint for JSON response
        const cartAddUrl = '/cart/add.js';
        console.log('Adding to cart:', cartAddUrl, 'variant ID:', variantId, 'quantity:', quantity, 'upsells:', upsellIds);

        let response;
        if (upsellIds.length > 0) {
          const mainItem = { id: parseInt(variantId, 10), quantity: quantity };
          const properties = {};
          for (const [key, value] of formData.entries()) {
            const m = key.match(/^properties\[(.+)\]$/);
            if (m) properties[m[1]] = value;
          }
          if (Object.keys(properties).length) mainItem.properties = properties;
          if (formData.get('selling_plan')) mainItem.selling_plan = formData.get('selling_plan');
          // A4 (audit 2026-10-08): gift lines carry a hidden `_gift_for` property (the main
          // variant that unlocked them) so cart-drawer.js can remove them if that product is
          // removed from the cart. Underscore-prefixed properties are hidden at checkout.
          const items = [mainItem].concat(upsellIds.map(id => {
            const item = { id: id, quantity: 1 };
            if (giftIds.indexOf(id) !== -1) item.properties = { _gift_for: String(variantId) };
            return item;
          }));
          response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          });
        } else {
          response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
            },
            body: new URLSearchParams(formData).toString()
          });
        }

        console.log('Response status:', response.status);
        console.log('Response content-type:', response.headers.get('content-type'));

        // Get response text first to debug
        const responseText = await response.text();
        console.log('Response text:', responseText.substring(0, 500));

        // Check if response is OK
        if (!response.ok) {
          console.error('Response error:', responseText);
          throw new Error('Network response was not ok: ' + response.status);
        }

        // Parse JSON
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('JSON parse error:', parseError, 'Response was:', responseText);
          throw new Error('Server returned invalid JSON');
        }

        console.log('Cart response:', data);

        // Handle different response formats
        if (data.status === 'error' || data.error) {
          const errorMsg = data.description || data.error || data.message || window.cartStrings.error;
          this.handleErrorMessage(errorMsg);
          return;
        }

        this.handleErrorMessage();

        // Items-mode (/cart/add.js with {items}) responses carry no item_count —
        // fetch the cart so the count bubble stays accurate. Then reset the toggles
        // so a second main ATC click doesn't silently re-add the upsells.
        if (upsellIds.length > 0) {
          try {
            const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
            data.item_count = cart.item_count;
          } catch (e) { /* bubble count best-effort */ }
          this.getSectionUpsells().forEach(u => { if (u.dataset.selected === 'true') u.dataset.selected = 'false'; });
        }

        // Dispatch cart updated event
        this.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: data
        }));

        // Skip cart → send straight to checkout when the buy-buttons block has skip_cart
        // enabled (the main variant input carries data-skip-cart="true"). Mirrors the
        // bundle-deals.addBundle() path in chug-components.js. Must run before the drawer.
        const skipCart = this.form.querySelector('input[name="id"][data-skip-cart="true"], input.product-variant-id[data-skip-cart="true"]');
        if (skipCart) {
          window.location.href = '/checkout';
          return;
        }

        // Open cart drawer if using drawer — populate it BEFORE opening so it never
        // flashes/stays blank (the cart may have been empty on page load).
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          await this.updateCartDrawer(data);
          drawer.open();
        } else {
          window.location.href = window.routes.cart_url;
        }

      } catch (error) {
        console.error('Error adding to cart:', error);
        this.handleErrorMessage(error.message || window.cartStrings.error);
      } finally {
        this._submitting = false;
        buttons.forEach(btn => {
          btn.disabled = false;
          btn.classList.remove('loading');
        });
      }
    }

    async updateCartDrawer(cartData) {
      try {
        console.log('Updating cart drawer with data:', cartData);

        const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
        const html = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newCartDrawer = doc.querySelector('#CartDrawer');
        const newOverlay = doc.querySelector('#CartDrawer-Overlay');

        const oldCartDrawer = document.getElementById('CartDrawer');
        const oldOverlay = document.getElementById('CartDrawer-Overlay');

        if (newCartDrawer && oldCartDrawer) {
          oldCartDrawer.innerHTML = newCartDrawer.innerHTML;
          console.log('Cart drawer innerHTML updated');
        } else {
          console.warn('Cart drawer elements not found:', { newCartDrawer: !!newCartDrawer, oldCartDrawer: !!oldCartDrawer });
        }

        if (newOverlay && oldOverlay) {
          oldOverlay.innerHTML = newOverlay.innerHTML;
          console.log('Cart drawer overlay updated');
        }

        // We just added an item, so the cart is non-empty — drop the "is-empty" class the
        // <cart-drawer> may have carried from an empty page load (it can hide items/footer).
        const drawerEl = document.querySelector('cart-drawer');
        if (drawerEl) drawerEl.classList.remove('is-empty');

        // Update cart count bubble
        const newCount = cartData.item_count || 0;
        const countBubbles = document.querySelectorAll('.cart-count-bubble span:first-child');
        countBubbles.forEach(bubble => {
          bubble.textContent = newCount < 100 ? newCount : '99+';
        });

        console.log('Cart drawer updated successfully, item count:', newCount);

      } catch (error) {
        console.error('Error updating cart drawer:', error);
        window.location.reload();
      }
    }

    onSkipCartClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const buttons = this.querySelectorAll('button[type="submit"]');
      buttons.forEach(btn => btn.disabled = true);

      const formData = new FormData();
      const variantInput = this.form.querySelector('input[name="id"]');

      if (variantInput) formData.append('id', variantInput.value);
      formData.append('quantity', this.getSelectedQuantity());

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
        },
        body: new URLSearchParams(formData).toString()
      }).then(response => response.json())
        .then(data => {
          // Secondary "skip cart" button → straight to checkout (its whole purpose).
          window.location.href = '/checkout';
        })
        .catch(error => {
          console.error('Error:', error);
          buttons.forEach(btn => btn.disabled = false);
        });
    }

    // Upsell toggles (upsell-system.js) that live in this product's section. Excludes
    // the upsells' own nested <product-form>s — only the MAIN form collects them.
    getSectionUpsells() {
      if (this.closest('product-info-upsell, cart-drawer-upsell, .quantity-gift')) return [];
      const scope = this.closest('.shopify-section') || document;
      return Array.from(scope.querySelectorAll('product-info-upsell[data-toggle="true"]'));
    }

    getSelectedUpsellIds() {
      return this.getSectionUpsells()
        .filter(u => u.dataset.selected === 'true' && u.dataset.id)
        .map(u => parseInt(u.dataset.id, 10))
        .filter(Boolean);
    }

    // Unlocked quantity-gifts in this section, minus any already in the cart
    // (re-adding would bump the gift line's quantity on a second ATC).
    async getUnlockedGiftIds() {
      if (this.closest('product-info-upsell, cart-drawer-upsell, .quantity-gift')) return [];
      const scope = this.closest('.shopify-section') || document;
      const qg = scope.querySelector('quantity-gifts');
      if (!qg || typeof qg.unlockedVariantIds !== 'function') return [];
      let ids = qg.unlockedVariantIds();
      if (!ids.length) return [];
      try {
        const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
        const inCart = new Set(cart.items.map(i => i.variant_id));
        ids = ids.filter(id => !inCart.has(id));
      } catch (e) { /* best-effort */ }
      return ids;
    }

    // Upsells/gifts attached to the SELECTED quantity-break tier (chug-components.js
    // quantity-break-upsells) — each tier label wraps its own upsell list.
    getTierUpsellIds() {
      if (this.closest('product-info-upsell, cart-drawer-upsell, .quantity-gift')) return [];
      const scope = this.closest('.shopify-section') || document;
      const checked = scope.querySelector('input[name="quantity"][type="radio"]:checked');
      if (!checked) return [];
      const label = scope.querySelector(`label[data-input="${checked.dataset.input}"]`) || checked.closest('label');
      const qbu = label && label.querySelector('quantity-break-upsells');
      return (qbu && typeof qbu.activeVariantIds === 'function') ? qbu.activeVariantIds() : [];
    }

    // Resolve the quantity to add. Supports the Shrine bundle / quantity-breaks picker
    // (radios named "quantity" inside <quantity-breaks>, a sibling of this form), and a
    // standard quantity input/stepper. Scoped to this product's section.
    getSelectedQuantity() {
      const scope = this.closest('.shopify-section') || document;
      const el = scope.querySelector('input[name="quantity"]:checked')
              || this.form.querySelector('input[name="quantity"]')
              || scope.querySelector('.quantity__input, quantity-input input[name="quantity"]');
      const v = el && el.value ? parseInt(el.value, 10) : 1;
      return (v && v > 0) ? v : 1;
    }

    handleErrorMessage(message) {
      if (!this.errorMessages || !this.errorMessage) return;

      this.errorMessages.hidden = !message;
      this.errorMessage.textContent = message || '';
    }

    handleSubmit(event) {
      if (event) {
        event.preventDefault();
      }
      this.onSubmit(event);
    }

    // All buttons that submit this form: the ones inside <product-form> plus any bound
    // from outside via the HTML form="" attribute (sticky bar, section ATC buttons).
    getSubmitButtons() {
      const list = Array.from(this.querySelectorAll('button[type="submit"]'));
      if (this.form && this.form.id) {
        document.querySelectorAll('button[form="' + this.form.id + '"]').forEach(b => { if (list.indexOf(b) === -1) list.push(b); });
      }
      // sticky bar / section ATC buttons wired through theme.liquid (id="SectionAtcBtn-<section>")
      const sec = this.dataset.section || (this.closest('.shopify-section') || {}).id;
      document.querySelectorAll('[id^="SectionAtcBtn-"]').forEach(b => {
        if (list.indexOf(b) === -1 && (!sec || b.id.indexOf(sec.replace(/^shopify-section-/, '')) !== -1)) list.push(b);
      });
      return list;
    }
  });
}
// skipcart-fix v2
